import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { BadgeComponent } from '@workbuddy/ui/components/badge';
import { STATUS_TONE_STYLES } from '../../../shared/status.tag.tones';
import { VisitApprovalProgressStore } from '../../board/visit.approval.progress.store';
import { VisitApprovalReviewBoardRow } from '../../visit.approval.board.model';
import { visitApprovalProgressBadge, VisitApprovalProgressBadge } from '../../visit.approval.progress.presentation';

/** The fetched review status, for rows the running operation has not reached. */
const FETCHED_TONES = {
  Awaiting: 'neutral',
  Approved: 'success',
  Changed: 'warning',
  Unavailable: 'error',
} as const;

/**
 * One review row's approval standing, live while an operation runs.
 *
 * Live state WINS over the fetched column: the fetched value is whatever the last board load saw, and
 * the whole point of this renderer is that a planner watching a 400-row approval sees each row move
 * without waiting for that load to happen again. When no operation covers the row, the fetched status
 * is all there is and the badge falls back to it.
 */
@Component({
  selector: 'wb-approval-progress',
  imports: [BadgeComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <w-badge
      [label]="badge().label"
      [color]="style().color"
      [backgroundColor]="style().backgroundColor"
      [borderColor]="style().borderColor"
      [tooltip]="badge().detail ?? badge().label"
      [iconLeft]="badge().busy ? 'fa-regular fa-spinner-third fa-spin' : ''"
    />
  `,
  styleUrl: './approval.progress.component.less',
})
export class ApprovalProgressComponent implements ICellRendererAngularComp {
  private readonly progress = inject(VisitApprovalProgressStore, { optional: true });
  private readonly occurrenceId = signal('');
  private readonly fetched = signal<VisitApprovalReviewBoardRow['approvalStatus'] | undefined>(undefined);
  private readonly fetchedReason = signal<string | undefined>(undefined);

  readonly badge = computed<VisitApprovalProgressBadge>(() => {
    const occurrenceId = this.occurrenceId();
    const live = occurrenceId ? this.progress?.snapshot().get(occurrenceId) : undefined;
    if (live) return visitApprovalProgressBadge(live.state);
    const status = this.fetched();
    if (!status) return { label: '', tone: 'neutral', busy: false };
    return { label: status, tone: FETCHED_TONES[status], busy: false, detail: this.fetchedReason() };
  });

  readonly style = computed(() => STATUS_TONE_STYLES[this.badge().tone]);

  agInit(params: ICellRendererParams<VisitApprovalReviewBoardRow>): void {
    this.occurrenceId.set(params.data?.occurrenceId ?? '');
    this.fetched.set(params.data?.approvalStatus);
    this.fetchedReason.set(params.data?.approvalUnavailableReason);
  }

  /**
   * Returning true keeps this component instance alive across grid refreshes.
   *
   * Destroying it would drop the signal subscription the live badge depends on, so a row that had
   * started moving would freeze until the next full board load recreated the cell.
   */
  refresh(params: ICellRendererParams<VisitApprovalReviewBoardRow>): boolean {
    this.agInit(params);
    return true;
  }
}
